/**
 * Adapters between Places API (New) results and the shapes the address form already consumes.
 *
 * The form predates Places API (New) and was written against the legacy AutocompleteService
 * prediction and Geocoder result. These keep that contract so callers do not change.
 */

export interface PlaceType {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export interface AddressSelection {
  street_number?: string;
  route?: string;
  locality?: string;
  sublocality?: string;
  neighborhood?: string;
  administrative_area_level_2?: string;
  administrative_area_level_1?: string;
  postal_code?: string;
  formatted_address?: string;
  lat?: number;
  lng?: number;
}

/**
 * The legacy `types: ['address']` collection does not exist in Places API (New). These are the
 * primary types that together cover precise addresses.
 */
export const ADDRESS_PRIMARY_TYPES = ['street_address', 'premise', 'subpremise'];

interface FormattableText {
  text: string;
}

interface SuggestionLike {
  placePrediction?: {
    placeId: string;
    text: FormattableText;
    mainText?: FormattableText | null;
    secondaryText?: FormattableText | null;
  } | null;
}

export function toPlaceTypes(suggestions: SuggestionLike[]): PlaceType[] {
  return suggestions.flatMap(({ placePrediction: p }) =>
    p
      ? [{
          place_id: p.placeId,
          description: p.text.text,
          structured_formatting: {
            main_text: p.mainText?.text ?? p.text.text,
            secondary_text: p.secondaryText?.text ?? '',
          },
        }]
      : []
  );
}

interface PlaceLike {
  formattedAddress?: string | null;
  location?: { lat: () => number; lng: () => number } | null;
  addressComponents?: Array<{ longText: string | null; shortText: string | null; types: string[] }> | null;
}

export function toAddressSelection(place: PlaceLike): AddressSelection {
  const selection: AddressSelection = {};
  if (place.formattedAddress) selection.formatted_address = place.formattedAddress;
  if (place.location) {
    selection.lat = place.location.lat();
    selection.lng = place.location.lng();
  }

  for (const { longText, shortText, types } of place.addressComponents ?? []) {
    const long = longText ?? undefined;
    if (types.includes('street_number')) selection.street_number = long;
    if (types.includes('route')) selection.route = long;
    if (types.includes('locality')) selection.locality = long;
    if (types.includes('sublocality') || types.includes('sublocality_level_1')) selection.sublocality = long;
    if (types.includes('neighborhood')) selection.neighborhood = long;
    if (types.includes('administrative_area_level_2')) selection.administrative_area_level_2 = long;
    if (types.includes('administrative_area_level_1')) selection.administrative_area_level_1 = shortText ?? undefined;
    if (types.includes('postal_code')) selection.postal_code = long;
  }

  return selection;
}
